import React, { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { 
  procedureTypeService, 
  ProcedureType, 
  ProcedureTypeData, 
  ProcedureTypeFilters 
} from '@/services/procedureTypeService';
import { useToast } from '@/hooks/use-toast';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search,
  AlertTriangle,
  Check,
  Settings,
  Activity,
  X
} from 'lucide-react';

// Table columns configuration
const columns = [
  {
    name: 'ID',
    selector: (row: ProcedureType) => row.id,
    sortable: true,
    width: '80px',
  },
  {
    name: 'Nume (română)',
    selector: (row: ProcedureType) => row.name_ro,
    sortable: true,
    grow: 2,
  },
  {
    name: 'Nume (engleză)',
    selector: (row: ProcedureType) => row.name_en,
    sortable: true,
    grow: 2,
  },
  {
    name: 'Utilizări',
    selector: (row: ProcedureType) => row.usage_count || 0,
    sortable: true,
    width: '100px',
    cell: (row: ProcedureType) => (
      <Badge variant="secondary">
        {row.usage_count || 0}
      </Badge>
    ),
  },
  {
    name: 'Creat la',
    selector: (row: ProcedureType) => row.created_at || '',
    sortable: true,
    width: '120px',
    cell: (row: ProcedureType) => 
      row.created_at ? new Date(row.created_at).toLocaleDateString('ro-RO') : '-',
  },
];

export const ProcedureTypesManagement: React.FC = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [procedureTypes, setProcedureTypes] = useState<ProcedureType[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedType, setSelectedType] = useState<ProcedureType | null>(null);
  
  // Form data
  const [formData, setFormData] = useState<ProcedureTypeData>({
    name_ro: '',
    name_en: ''
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    loadData();
  }, [currentPage, perPage, searchTerm]);

  const loadData = async () => {
    try {
      setLoading(true);
      const filters: ProcedureTypeFilters = {
        page: currentPage,
        limit: perPage,
        search: searchTerm.trim() || undefined,
        sortBy: 'name_ro',
        sortOrder: 'asc'
      };

      const response = await procedureTypeService.listProcedureTypes(filters);
      setProcedureTypes(response.data);
      setTotalRows(response.total);
      setLoading(false);
    } catch (error: any) {
      console.error('Error loading procedure types:', error);
      toast({
        title: "Eroare",
        description: error.message || 'Eroare la încărcarea tipurilor de proceduri',
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!formData.name_ro.trim()) {
      errors.name_ro = 'Numele în română este obligatoriu';
    } else if (formData.name_ro.trim().length < 2) {
      errors.name_ro = 'Numele trebuie să aibă cel puțin 2 caractere';
    }

    if (!formData.name_en.trim()) {
      errors.name_en = 'Numele în engleză este obligatoriu';
    } else if (formData.name_en.trim().length < 2) {
      errors.name_en = 'Numele trebuie să aibă cel puțin 2 caractere';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddType = async () => {
    if (!validateForm()) return;

    try {
      setActionLoading(true);
      await procedureTypeService.createProcedureType(formData);
      
      toast({
        title: "Succes",
        description: 'Tipul de procedură a fost adăugat cu succes',
      });
      
      setShowAddDialog(false);
      resetForm();
      await loadData();
      setActionLoading(false);
    } catch (error: any) {
      console.error('Error creating procedure type:', error);
      toast({
        title: "Eroare",
        description: error.message || 'Eroare la adăugarea tipului de procedură',
        variant: "destructive",
      });
      setActionLoading(false);
    }
  };

  const handleEditType = async () => {
    if (!selectedType || !validateForm()) return;

    try {
      setActionLoading(true);
      await procedureTypeService.updateProcedureType(selectedType.id, formData);
      
      toast({
        title: "Succes",
        description: 'Tipul de procedură a fost actualizat cu succes',
      });
      
      setShowEditDialog(false);
      resetForm();
      await loadData();
      setActionLoading(false);
    } catch (error: any) {
      console.error('Error updating procedure type:', error);
      toast({
        title: "Eroare",
        description: error.message || 'Eroare la actualizarea tipului de procedură',
        variant: "destructive",
      });
      setActionLoading(false);
    }
  };

  const handleDeleteType = async () => {
    if (!selectedType) return;

    try {
      setActionLoading(true);
      await procedureTypeService.deleteProcedureType(selectedType.id);
      
      toast({
        title: "Succes",
        description: 'Tipul de procedură a fost șters cu succes',
      });
      
      setShowDeleteDialog(false);
      setSelectedType(null);
      await loadData();
      setActionLoading(false);
    } catch (error: any) {
      console.error('Error deleting procedure type:', error);
      toast({
        title: "Eroare",
        description: error.message || 'Eroare la ștergerea tipului de procedură',
        variant: "destructive",
      });
      setActionLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name_ro: '',
      name_en: ''
    });
    setFormErrors({});
    setSelectedType(null);
  };

  const openEditDialog = (type: ProcedureType) => {
    setSelectedType(type);
    setFormData({
      name_ro: type.name_ro,
      name_en: type.name_en
    });
    setFormErrors({});
    setShowEditDialog(true);
  };

  const openDeleteDialog = (type: ProcedureType) => {
    setSelectedType(type);
    setShowDeleteDialog(true);
  };

  const openAddDialog = () => {
    resetForm();
    setShowAddDialog(true);
  };

  // Actions column for the table
  const actionsColumn = {
    name: 'Acțiuni',
    cell: (row: ProcedureType) => (
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => openEditDialog(row)}
          title="Editează tipul de procedură"
        >
          <Edit className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => openDeleteDialog(row)}
          title="Șterge tipul de procedură"
          className="text-red-600 hover:text-red-700"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    ),
    ignoreRowClick: true,
    allowOverflow: true,
    button: true,
    width: '120px',
  };

  const tableColumns = [...columns, actionsColumn];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  const handleSearch = () => {
    setSearchTerm(searchInput.trim());
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  if (loading && currentPage === 1) {
    return (
      <MainLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-96 w-full" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Tipuri proceduri</h1>
            <p className="text-muted-foreground">
              Gestionează tipurile de proceduri disponibile în sistem
            </p>
          </div>
          <Button onClick={openAddDialog}>
            <Plus className="mr-2 h-4 w-4" />
            Adaugă tip procedură
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle>Filtrare și căutare</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 flex-1 max-w-md">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Caută tipuri proceduri..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                />
                <Button
                  onClick={handleSearch}
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <Search className="h-4 w-4" />
                  Caută
                </Button>
                {searchTerm && (
                  <Button
                    onClick={handleClearSearch}
                    size="sm"
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <X className="h-4 w-4" />
                    Șterge
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Tipuri proceduri ({totalRows})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={tableColumns}
              data={procedureTypes}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationDefaultPage={currentPage}
              paginationPerPage={perPage}
              onChangeRowsPerPage={handlePerRowsChange}
              onChangePage={handlePageChange}
              progressPending={loading}
              noDataComponent="Nu există tipuri de proceduri înregistrate"
              paginationComponentOptions={{
                rowsPerPageText: 'Rânduri pe pagină:',
                rangeSeparatorText: 'din',
                selectAllRowsItem: true,
                selectAllRowsItemText: 'Toate',
              }}
            />
          </CardContent>
        </Card>

        {/* Add Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Adaugă tip procedură nou
              </DialogTitle>
              <DialogDescription>
                Completează informațiile pentru noul tip de procedură
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="name_ro">Nume (română)*</Label>
                <Input
                  id="name_ro"
                  value={formData.name_ro}
                  onChange={(e) => setFormData({...formData, name_ro: e.target.value})}
                  placeholder="ex. Chirurgie cardiacă"
                />
                {formErrors.name_ro && (
                  <p className="text-sm text-red-600">{formErrors.name_ro}</p>
                )}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="name_en">Nume (engleză)*</Label>
                <Input
                  id="name_en"
                  value={formData.name_en}
                  onChange={(e) => setFormData({...formData, name_en: e.target.value})}
                  placeholder="ex. Cardiac Surgery"
                />
                {formErrors.name_en && (
                  <p className="text-sm text-red-600">{formErrors.name_en}</p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)} disabled={actionLoading}>
                Anulează
              </Button>
              <Button onClick={handleAddType} disabled={actionLoading}>
                {actionLoading ? (
                  <>
                    <Settings className="mr-2 h-4 w-4 animate-spin" />
                    Adaugă...
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    Adaugă
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5" />
                Editează tip procedură
              </DialogTitle>
              <DialogDescription>
                Modifică informațiile tipului de procedură
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="edit_name_ro">Nume (română)*</Label>
                <Input
                  id="edit_name_ro"
                  value={formData.name_ro}
                  onChange={(e) => setFormData({...formData, name_ro: e.target.value})}
                />
                {formErrors.name_ro && (
                  <p className="text-sm text-red-600">{formErrors.name_ro}</p>
                )}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit_name_en">Nume (engleză)*</Label>
                <Input
                  id="edit_name_en"
                  value={formData.name_en}
                  onChange={(e) => setFormData({...formData, name_en: e.target.value})}
                />
                {formErrors.name_en && (
                  <p className="text-sm text-red-600">{formErrors.name_en}</p>
                )}
              </div>
              {selectedType && selectedType.usage_count && selectedType.usage_count > 0 && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Acest tip de procedură este folosit în {selectedType.usage_count} proceduri.
                    Modificările vor afecta toate procedurile existente.
                  </AlertDescription>
                </Alert>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)} disabled={actionLoading}>
                Anulează
              </Button>
              <Button onClick={handleEditType} disabled={actionLoading}>
                {actionLoading ? (
                  <>
                    <Settings className="mr-2 h-4 w-4 animate-spin" />
                    Salvează...
                  </>
                ) : (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Salvează
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                Confirmare ștergere
              </DialogTitle>
              <DialogDescription>
                <div className="space-y-2">
                  <p>Ești sigur că vrei să ștergi tipul de procedură <strong>{selectedType?.name_ro}</strong>?</p>
                  {selectedType && selectedType.usage_count && selectedType.usage_count > 0 && (
                    <Alert variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>Atenție!</strong> Acest tip de procedură este folosit în {selectedType.usage_count} proceduri.
                        Ștergerea va afecta toate aceste proceduri și poate cauza probleme în sistem.
                      </AlertDescription>
                    </Alert>
                  )}
                  <p className="text-red-600 font-medium">
                    Această acțiune este ireversibilă.
                  </p>
                </div>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={actionLoading}>
                Anulează
              </Button>
              <Button variant="destructive" onClick={handleDeleteType} disabled={actionLoading}>
                {actionLoading ? (
                  <>
                    <Settings className="mr-2 h-4 w-4 animate-spin" />
                    Șterge...
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Șterge definitiv
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};